import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { EventInventoryRecord, selectSelectedInventoryItems } from "@src/store/pricing/queryEventInventoryAllSlice";
import { pricingActions } from "@src/store";
import { Button, ButtonVariant } from "../button/Button";
import { Switch } from "../switch/Switch";
import helperStyles from "@src/components/helpers.module.scss";
import { clx } from "../utils/stringUtils";
import BulkEditCostItem from "./BulkEditCostItem";
import EventContextHeader, { getListingEvents } from "@src/components/eventContextHeader/EventContextHeader";
import styles from "./BulkEditCost.module.scss";

type TBulkEditCost = {
	handleClose?: () => void;
};

type TState = {
	items: Array<EventInventoryRecord>;
	costInput: string;
	costLocked: boolean;
};

const BulkEditCost = ({ handleClose }: TBulkEditCost) => {
	const dispatch = useAppDispatch();

	const items = useAppSelector(selectSelectedInventoryItems);
	const { loading: updateLoading } = useAppSelector((state) => state.pricing.updateListingCosts);

	const computeLowestPerTicketCost = (list: Array<EventInventoryRecord>): string => {
		const perTicketCosts = list
			.map((x) => {
				const qty = x.Quantity || 1;
				return qty ? x.Item_Cost / qty : 0;
			})
			.filter((v) => Number.isFinite(v) && v > 0);
		if (!perTicketCosts.length) return "";
		return Math.min(...perTicketCosts).toFixed(2);
	};

	const [state, setState] = useState<TState>({
		items,
		costInput: computeLowestPerTicketCost(items),
		costLocked: false,
	});

	useEffect(() => {
		setState((prev) => ({
			...prev,
			items,
			costInput: prev.costInput || computeLowestPerTicketCost(items),
		}));
	}, [items]);

	const handleCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setState((prev) => ({ ...prev, costInput: e.target.value }));
	};

	const handleToggleLock = () => {
		setState((prev) => ({ ...prev, costLocked: !prev.costLocked }));
	};

	const handleRemoveItem = (listingId: number) => {
		setState((prev) => ({ ...prev, items: prev.items.filter((x) => x.Listing_ID !== listingId) }));
	};

	const parsedCost = Number.parseFloat(state.costInput);
	const isValidCost = !isNaN(parsedCost) && state.costInput.trim().length > 0 && parsedCost >= 0;
	const newCostValue = isValidCost ? parsedCost : null;

	const handleSubmit = async () => {
		if (!state.items.length || !isValidCost || newCostValue == null) return;

		const CostUpdates = state.items.map((x) => ({
			cost: newCostValue,
			listing_id: x.Listing_ID,
		}));

		try {
			await dispatch(
				pricingActions.updateListingCosts({
					CostUpdates,
					CostLocked: state.costLocked,
				}),
			).unwrap();
			toast.success("Cost updated.");
			handleClose?.();
		} catch {
			toast.error("Failed to update cost.");
		}
	};

	const mappedItems = state.items.map((x) => (
		<BulkEditCostItem key={x.Item_ID} data={x} newCost={newCostValue} onRemove={handleRemoveItem} />
	));

	return (
		<div className={styles.root}>
			<EventContextHeader
				padding
				listings={state.items.map((x) => ({ section: x.Section, row: x.Row, seats: x.Seats }))}
				events={getListingEvents(state.items)}
			/>
			<div className={styles.top}>
				<div className={styles.left}>
					<div className={clx(styles.subHeader, styles.mb)}>Pricing Rules</div>
					<div className={styles.fieldLabel}>New Cost Per Ticket</div>
					<div className={styles.inputRow}>
						<select className={styles.currencySelect} value="USD" onChange={() => void 0}>
							<option value="USD">$ USD</option>
						</select>
						<input
							className={clx(styles.costInput, state.costInput && !isValidCost ? styles.invalid : "")}
							type="number"
							inputMode="decimal"
							min={0}
							step="1"
							placeholder="0.00"
							value={state.costInput}
							onChange={handleCostChange}
						/>
					</div>
					<div className={styles.lockRow}>
						<Switch checked={state.costLocked} onChange={handleToggleLock} label="Lock cost" />
					</div>
					<div className={styles.helperText}>
						Locking the cost prevents automatic repricing tools from dynamically lowering your list price below
						this threshold.
					</div>
					{state.items.length > 1 ? (
						<div className={styles.warning}>
							<div className={styles.warningHeader}>Warning! You have multiple listings selected.</div>
							<div className={styles.warningText}>
								Saving will overwrite any previous Edit Cost settings saved to these listings.
							</div>
						</div>
					) : null}
				</div>
				<div className={styles.right}>
					<div className={styles.rightTopHeaders}>
						<div className={styles.subHeader}>Changes Summary</div>
						<div className={styles.subHeaderSecondary}>Applying to {state.items.length} items</div>
					</div>
					<div data-no-border="true" className={styles.singleListing}>
						<div className={clx(styles.secRowSeat, styles.headerSecRowSeat)}>
							<div className={clx(styles.itemTextPrimary, helperStyles.ellipsisDiv)}>Sec, Row, Seat</div>
						</div>
						<div className={styles.qty}>Qty</div>
						<div className={styles.currentCost}>Current Cost</div>
						<div className={styles.newCost}>New Cost</div>
						<div className={styles.xContainer}></div>
					</div>
					<div className={styles.listings}>{mappedItems}</div>
				</div>
			</div>
			<div className={styles.buttons}>
				<Button onClick={handleClose} variant={ButtonVariant.Secondary} disabled={updateLoading}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					isLoading={updateLoading}
					disabled={!state.items.length || !isValidCost || updateLoading}>
					Save changes
				</Button>
			</div>
		</div>
	);
};

export default BulkEditCost;
